import React, { Component } from 'react';
import { Card, Table, Button, Tag, message } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import intl from 'react-intl-universal';

import { withAppContext } from 'context/AppContext';
import { getSessions, revokeSession } from 'utils/apis/authentication';
import CloseButton from 'components/CloseButton/CloseButton';

const USER_AGENT_MAX_LENGTH = 60;

class Sessions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sessions: [],
      loading: true,
      revokingId: null
    };
  }

  componentDidMount() {
    const { match, user } = this.props;
    const userId = match.params.id;
    if (!user || String(user._id) !== String(userId)) {
      this.props.history.push('/forbidden');
      return;
    }
    this.fetchSessions();
  }

  fetchSessions = () => {
    this.setState({ loading: true });
    getSessions()
      .then(data => {
        this.setState({ sessions: data.sessions || [], loading: false });
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error(intl.get('sessions.loadError').d('Failed to load sessions.'));
      });
  };

  handleRevoke = (session, isCurrent) => {
    const { history, logout } = this.props;
    this.setState({ revokingId: session.id });
    revokeSession(session.id)
      .then(response => {
        if (response.status === 204) {
          if (isCurrent) {
            logout();
            message.info(intl.get('sessions.signedOutThisDevice').d('You have been signed out on this device.'));
            history.push('/');
          } else {
            this.fetchSessions();
            message.success(intl.get('sessions.revokedMessage').d('Session revoked.'));
          }
        }
      })
      .catch(err => {
        if (err.response && err.response.status === 404) {
          this.fetchSessions();
        } else {
          message.error(intl.get('sessions.revokeError').d('Failed to revoke session.'));
        }
      })
      .finally(() => {
        this.setState({ revokingId: null });
      });
  };

  handleRevokeAllOthers = () => {
    const { sessions } = this.state;
    const currentSession = sessions.find(s => s.current);
    const toRevoke = sessions.filter(s => !s.current && !s.revokedAt);
    if (toRevoke.length === 0) {
      message.info(intl.get('sessions.noOthersToRevoke').d('No other sessions to revoke.'));
      return;
    }
    Promise.all(toRevoke.map(s => revokeSession(s.id)))
      .then(() => {
        this.fetchSessions();
        message.success(intl.get('sessions.allOthersRevoked').d('All other sessions have been revoked.'));
      })
      .catch(() => {
        this.fetchSessions();
        message.error(intl.get('sessions.revokeError').d('Failed to revoke some sessions.'));
      });
  };

  truncate = (str, maxLen) => {
    if (!str || typeof str !== 'string') return '-';
    return str.length <= maxLen ? str : `${str.slice(0, maxLen)}…`;
  };

  render() {
    const { sessions, loading, revokingId } = this.state;
    const { match, user } = this.props;
    const userId = match.params.id;

    if (!user || String(user._id) !== String(userId)) {
      return null;
    }

    const otherActiveCount = sessions.filter(s => !s.current && !s.revokedAt).length;

    const columns = [
      {
        title: intl.get('sessions.ip').d('IP'),
        dataIndex: 'loginIp',
        key: 'loginIp',
        render: val => val || '-'
      },
      {
        title: intl.get('sessions.userAgent').d('Device'),
        dataIndex: 'userAgent',
        key: 'userAgent',
        render: val => this.truncate(val, USER_AGENT_MAX_LENGTH)
      },
      {
        title: intl.get('sessions.created').d('Login Time'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: val => (val ? moment(val).format('YYYY-MM-DD HH:mm') : '-')
      },
      {
        title: intl.get('sessions.lastActive').d('Last active'),
        dataIndex: 'lastActiveAt',
        key: 'lastActiveAt',
        render: val => (val ? moment(val).format('YYYY-MM-DD HH:mm') : '-')
      },
      {
        title: intl.get('sessions.status').d('Status'),
        key: 'status',
        render: (_, record) => {
          if (record.revokedAt) {
            return <Tag color="blue">{intl.get('sessions.revoked').d('Revoked')}</Tag>;
          }
          if (record.current) {
            return <Tag color="green">{intl.get('sessions.thisDevice').d('This device')}</Tag>;
          }

          return <Tag color="purple">{intl.get('sessions.otherDevice').d('Other Device')}</Tag>;
        }
      },
      {
        title: intl.get('sessions.actions').d('Actions'),
        key: 'actions',
        render: (_, record) => {
          if (record.revokedAt) return null;
          if (record.current) {
            return (
              <Button size="small" disabled>
                {intl.get('sessions.revoke').d('Revoke')}
              </Button>
            );
          }
          return (
            <Button size="small" danger loading={revokingId === record.id} onClick={() => this.handleRevoke(record, false)}>
              {intl.get('sessions.revoke').d('Revoke')}
            </Button>
          );
        }
      }
    ];

    return (
      <div>
        <CloseButton onClick={() => this.props.history.push(`/userprofile/${userId}`)} />
        <Card
          title={intl.get('sessions.title').d('Active sessions')}
          extra={
            otherActiveCount > 0 ? (
              <Button type="default" onClick={this.handleRevokeAllOthers}>
                {intl.get('sessions.revokeAllOthers').d('Revoke all other sessions')}
              </Button>
            ) : null
          }
        >
          <Table
            rowKey="id"
            loading={loading}
            dataSource={sessions}
            columns={columns}
            pagination={false}
            locale={{ emptyText: intl.get('sessions.empty').d('No sessions') }}
          />
        </Card>
      </div>
    );
  }
}

export default withAppContext(withRouter(Sessions));
